import { Injectable, inject } from '@angular/core';
import { InputPermissionsService } from '@app/inputs/services/input-permissions.service';
import { AddToPathwayModalService } from '@app/pathways/services/add-to-pathway-modal.service';
import { RecommendingItem } from '@app/recommendations/recommendations.model';
import { RecommendationsModalService } from '@app/recommendations/services/recommendations-modal.service';
import { TargetService } from '@app/target/services/target.service';
import {
  AssessContentActionId,
  AssessContentActions,
  AssessContentTarget,
} from '@degreed/lxp/assess/services';
import { AuthService } from '@degreed/lxp/services/authorization';

/**
 * Binds the assessment editor's "More actions" menu (`AssessContentActions`,
 * `libs/lxp/features/assess/services`) to the same app-only services the
 * content-catalog and content-card "..." menus already use for Share / Add to
 * Pathway / Add to Job Role (`content-catalog-menu.service.ts`,
 * `content-card.service.ts`) — same item shape, same permission checks, so an
 * assessment behaves exactly like any other catalog item in these three flows.
 */
@Injectable()
export class LxpAssessContentActionsAdapter extends AssessContentActions {
  private readonly inputPermissions = inject(InputPermissionsService);
  private readonly recommendationsModalService = inject(
    RecommendationsModalService
  );
  private readonly addToPathwayModalService = inject(AddToPathwayModalService);
  private readonly targetService = inject(TargetService);
  private readonly authService = inject(AuthService);

  public override available(
    // Assess passes no per-item permissions object yet (D-29), so availability
    // depends only on resourceType/inputType, not on which assessment this is.
    _target: AssessContentTarget
  ): AssessContentActionId[] {
    const authUser = this.authService.authUser;
    const item = this.toPermissionCheckItem();
    const ids: AssessContentActionId[] = [];

    if (
      this.inputPermissions.canShareItem(
        item,
        () => !!authUser?.canRecommendItems
      )
    ) {
      ids.push('share');
    }
    if (this.inputPermissions.canAddItemToPathway(item)) {
      ids.push('addToPathway');
    }
    if (this.inputPermissions.canAddItemToTarget(item)) {
      ids.push('addToJobRole');
    }
    return ids;
  }

  public override run(
    id: AssessContentActionId,
    target: AssessContentTarget,
    trigger: HTMLElement
  ): void {
    const authUser = this.authService.authUser;
    const organizationId = authUser?.defaultOrgId;

    switch (id) {
      case 'share': {
        const item: RecommendingItem = {
          resourceId: target.inputId,
          resourceType: 'Assessment',
          title: target.title,
          organizationId,
          isEndorsed: false,
        };
        this.recommendationsModalService
          .showShareModal(item, trigger)
          .subscribe();
        break;
      }
      case 'addToPathway': {
        this.addToPathwayModalService.showAddToPathwayFormDrawer(
          target.inputId,
          'Assessment',
          organizationId,
          false,
          target.title
        );
        break;
      }
      case 'addToJobRole': {
        this.targetService
          .showAddToTargetModal(
            'JobRole',
            {
              title: target.title,
              organizationId,
              resourceType: 'Assessment',
              resourceId: target.inputId,
            },
            trigger
          )
          .subscribe();
        break;
      }
    }
  }

  /** `resourceType`/`inputType` only — the two fields every `InputPermissionsService`
   *  gate reads for an assessment (D-29: an assessment has no `permissions` object from
   *  the backend yet, so every check below falls through to its legacy calculation). */
  private toPermissionCheckItem() {
    return {
      resourceType: 'Assessment' as const,
      inputType: 'Assessment' as const,
    };
  }
}
